// Adapted from pingdotgg/t3code's queuedMessageStore (MIT), 719a76ca.
// Amoreg queues wait for a settled turn: a tool boundary must not cancel Eve.
import { create } from "zustand";
import type { ChatComposerHandle } from "./components/chat/ChatComposer";
import { randomUUID } from "./lib/utils";

export interface QueuedComposerMessage {
  id: string;
  prompt: string;
  context: ReturnType<ChatComposerHandle["getSendContext"]>;
  held: boolean;
}
interface QueuedMessageState {
  queues: Record<string, QueuedComposerMessage[]>;
  generations: Record<string, number>;
  enqueue: (key: string, message: Omit<QueuedComposerMessage, "id" | "held">) => void;
  take: (key: string, id: string) => QueuedComposerMessage | undefined;
  hold: (key: string) => void;
  restore: (key: string, message: QueuedComposerMessage) => void;
  /** Reorder: move `id` in front of `beforeId`, or to the end for `null`. */
  move: (key: string, id: string, beforeId: string | null) => void;
}
export const EMPTY_QUEUED_MESSAGES: QueuedComposerMessage[] = [];
export const useQueuedMessageStore = create<QueuedMessageState>((set, get) => ({
  queues: {},
  generations: {},
  enqueue: (key, message) =>
    set((state) => ({
      queues: {
        ...state.queues,
        [key]: [...(state.queues[key] ?? []), { ...message, id: randomUUID(), held: false }],
      },
    })),
  take: (key, id) => {
    const message = get().queues[key]?.find((entry) => entry.id === id);
    if (!message) return;
    set((state) => ({
      queues: { ...state.queues, [key]: state.queues[key]!.filter((entry) => entry.id !== id) },
    }));
    return message;
  },
  hold: (key) =>
    set((state) => ({
      generations: { ...state.generations, [key]: (state.generations[key] ?? 0) + 1 },
      queues: {
        ...state.queues,
        [key]: (state.queues[key] ?? []).map((message) => ({ ...message, held: true })),
      },
    })),
  restore: (key, message) =>
    set((state) => ({
      queues: {
        ...state.queues,
        [key]: [
          { ...message, held: true },
          ...(state.queues[key] ?? []).filter((entry) => entry.id !== message.id),
        ],
      },
    })),
  move: (key, id, beforeId) =>
    set((state) => {
      const queue = state.queues[key];
      const next = queue ? moveQueuedMessage(queue, id, beforeId) : undefined;
      return next && next !== queue ? { queues: { ...state.queues, [key]: next } } : state;
    }),
}));

// Returns the same array when the move is a no-op or names an entry that has
// already left the queue (sent or removed while a drag was in flight).
export function moveQueuedMessage<T extends { id: string }>(
  queue: T[],
  id: string,
  beforeId: string | null,
): T[] {
  if (id === beforeId) return queue;
  const moving = queue.find((entry) => entry.id === id);
  if (!moving) return queue;
  const rest = queue.filter((entry) => entry.id !== id);
  const insertAt =
    beforeId === null ? rest.length : rest.findIndex((entry) => entry.id === beforeId);
  if (insertAt === -1) return queue;
  const next = [...rest.slice(0, insertAt), moving, ...rest.slice(insertAt)];
  return next.every((entry, index) => entry === queue[index]) ? queue : next;
}

/**
 * The entry a row must be placed in front of when dropped at `insertIndex`
 * (0..queue.length), `null` for the end, or `undefined` when the drop leaves
 * the row where it is.
 */
export function queuedMessageDropTarget(
  ids: ReadonlyArray<string>,
  id: string,
  insertIndex: number,
): string | null | undefined {
  const index = ids.indexOf(id);
  if (index === -1 || insertIndex === index || insertIndex === index + 1) return undefined;
  return ids[insertIndex] ?? null;
}

export function isQueuedMessageDue(phase: string, held: boolean, blocked: boolean): boolean {
  return phase === "ready" && !held && !blocked;
}

/**
 * The phase the queue dispatches on. A failed turn leaves the session in
 * `error` (shown as `disconnected`), yet the server takes the next message on
 * it as usual; holding the queue there left a queued message sitting under
 * the error with the chat seemingly locked. So a session in error counts as
 * ready for the queue. A Stop still pauses it (`hold`), and a send the client
 * itself failed still blocks it.
 */
export function queueDispatchPhase(phase: string, sessionStatus: string | undefined): string {
  return phase === "disconnected" && sessionStatus === "error" ? "ready" : phase;
}
